import { apiClient } from './apiClient';
import { unwrap } from '../lib/apiErrors';

/**
 * Favorites service — saved archive posts (server-backed, paired with the
 * existing `archive.postsById` cache for full post bodies).
 *
 * Backend reference (frontend integration notes):
 *   GET    /v1/favorites/me                 → list (≤500, newest first)
 *   POST   /v1/favorites/me/posts/:postId   → idempotent add
 *   DELETE /v1/favorites/me/posts/:postId   → idempotent remove
 *   POST   /v1/favorites/me/sync            → bulk merge (≤500)
 *
 * The service layer keeps responses minimal — `(id, addedAt)` only — because
 * the archive cache (Redux `archive.postsById`) already holds full post
 * bodies. List rendering joins the two by id.
 */

export interface FavoriteEntry {
  /** Archive post id. */
  id: string;
  /** ISO timestamp when the favorite was created server-side. */
  addedAt: string;
}

interface FavoritesListResponse {
  posts: FavoriteEntry[];
}

interface SyncResult {
  added: number;
  skipped: number;
}

const MAX_FAVORITES = 500;

export const favoritesService = {
  /** List the authenticated user's saved posts. Newest first, capped at 500. */
  list: async (): Promise<FavoriteEntry[]> => {
    const res = await apiClient.get<FavoritesListResponse>('/favorites/me');
    const data = unwrap(res);
    return data?.posts ?? [];
  },

  /**
   * Idempotent add. Returns the server-assigned `addedAt` timestamp on
   * success. Backend returns 200 even if the post was already favorited
   * (no-op), and 404 if the post does not exist. Both are surfaced via the
   * standard apiClient error path.
   */
  add: async (postId: string): Promise<FavoriteEntry> => {
    const res = await apiClient.post<FavoriteEntry>(
      `/favorites/me/posts/${encodeURIComponent(postId)}`,
    );
    return unwrap(res);
  },

  /**
   * Idempotent remove. Backend returns 204 No Content; on success the api
   * client may return either an empty payload or a `success: true` shell —
   * both are fine, we don't read the body.
   */
  remove: async (postId: string): Promise<void> => {
    const res = await apiClient.delete<unknown>(
      `/favorites/me/posts/${encodeURIComponent(postId)}`,
    );
    if (!res.success) {
      // Tolerate "already removed" outcomes silently — DELETE is idempotent.
      // Surface only genuine errors (auth, network, etc.).
      throw new Error(res.error?.message || 'Failed to remove favorite');
    }
  },

  /**
   * Merge a list of post ids into the server set in one round trip. Used on
   * first login after guest browsing to migrate any localStorage-only
   * favorites. Capped at 500 ids — caller must slice if longer.
   * Returns counts of newly added vs. skipped (already present / invalid).
   */
  sync: async (postIds: string[]): Promise<SyncResult> => {
    const posts = postIds.slice(0, MAX_FAVORITES);
    const res = await apiClient.post<SyncResult>('/favorites/me/sync', { posts });
    return unwrap(res);
  },
};

export const FAVORITES_LIMIT = MAX_FAVORITES;
